import React, { useMemo, useState } from "react";
import { formatAiCredits } from "@/lib/formatAiCredits";
import { formatMonthlyUsd, retailSourceLabel } from "@/lib/partnerMargin";
import { getTierById } from "@/constants/aiTiersCatalog";

const STATUS_STYLES = {
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  provisioning: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  canceling: "bg-amber-500/10 text-amber-300 border-amber-500/40",
  suspended: "bg-white/5 text-partner-muted border-partner",
};

const formatUntilDate = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const CreditsCell = ({ client }) => {
  const used = client.creditsUsed ?? 0;
  const included = client.creditsIncluded ?? getTierById(client.tier)?.credits ?? 100;
  const overSoftCap = included > 0 && used > included;

  return (
    <div>
      <p className={`text-sm font-mono ${overSoftCap ? "text-amber-400" : "text-partner"}`}>
        {formatAiCredits(used)} / {formatAiCredits(included)}
      </p>
      {overSoftCap && (
        <span className="inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/30">
          Over soft cap
        </span>
      )}
    </div>
  );
};

const ClientRosterTable = ({
  clients,
  showFilters = true,
  isEmptyRoster = false,
  onChangeTier,
  onStopBilling,
  onResumeBilling,
  onReactivate,
  onOpenConsole,
  onSetRetail,
  openingConsoleClientId = null,
}) => {
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(() => {
    return clients.filter((client) => {
      const matchesSearch =
        !search ||
        client.name.toLowerCase().includes(search.toLowerCase()) ||
        client.email.toLowerCase().includes(search.toLowerCase());
      const matchesTier = tierFilter === "all" || String(client.tier) === tierFilter;
      const matchesStatus = statusFilter === "all" || client.status === statusFilter;
      return matchesSearch && matchesTier && matchesStatus;
    });
  }, [clients, search, tierFilter, statusFilter]);

  return (
    <div className="rounded-2xl border border-partner bg-[#11151F] overflow-hidden">
      {showFilters && (
        <div className="p-4 sm:p-5 border-b border-partner flex flex-col sm:flex-row gap-3">
          <input
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 partner-input rounded-lg px-4 py-2.5 text-sm font-body"
            data-testid="partner-clients-search"
          />
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="partner-input partner-select rounded-lg px-4 py-2.5 text-sm font-body sm:w-36"
          >
            <option value="all">All tiers</option>
            <option value="1">Tier 1</option>
            <option value="2">Tier 2</option>
            <option value="3">Tier 3</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="partner-input partner-select rounded-lg px-4 py-2.5 text-sm font-body sm:w-40"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="canceling">Canceling</option>
            <option value="provisioning">Provisioning</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px]" data-testid="partner-clients-table">
          <thead>
            <tr className="border-b border-partner text-left">
              {[
                "Name",
                "Digital Assistant",
                "Tier",
                "Credits used",
                "Retail",
                "Est. margin",
                "Status",
                "Created",
                "Last activity",
                "Actions",
              ].map((col) => (
                <th
                  key={col}
                  className="px-4 sm:px-5 py-3 text-[10px] font-mono tracking-wider text-partner-muted uppercase"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-5 py-12 text-center text-partner-muted font-body text-sm">
                  {isEmptyRoster || clients.length === 0
                    ? "No clients deployed yet."
                    : "No clients match your filters."}
                </td>
              </tr>
            ) : (
              filtered.map((client) => {
                const untilDate = formatUntilDate(client.billingStopAt);
                const isActive = client.status === "active";
                const isCanceling = client.status === "canceling";
                const isSuspended = client.status === "suspended";
                const isProvisioning = client.status === "provisioning";
                const canOpenConsole = isActive || isCanceling;
                const isOpeningConsole = openingConsoleClientId === client.id;

                return (
                  <tr
                    key={client.id}
                    className="border-b border-partner last:border-b-0 hover:bg-[#080A0F]/50"
                  >
                    <td className="px-4 sm:px-5 py-4">
                      <p className="text-sm font-semibold text-partner">{client.name}</p>
                      <p className="text-xs text-partner-muted font-body">{client.email}</p>
                    </td>
                    <td className="px-4 sm:px-5 py-4 text-sm text-partner font-body capitalize">
                      {client.vertical}
                    </td>
                    <td className="px-4 sm:px-5 py-4 text-sm text-partner font-mono">
                      Tier {client.tier}
                    </td>
                    <td className="px-4 sm:px-5 py-4">
                      <CreditsCell client={client} />
                    </td>
                    <td className="px-4 sm:px-5 py-4">
                      <div>
                        <p className="text-sm font-mono text-partner">
                          {formatMonthlyUsd(client.retailMonthlyUsd)}
                        </p>
                        {client.retailSource && (
                          <span
                            className={`inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-mono border ${
                              client.retailSource === "custom"
                                ? "bg-[#00D2FF]/10 text-partner-accent border-[#00D2FF]/30"
                                : "bg-white/5 text-partner-muted border-partner"
                            }`}
                          >
                            {retailSourceLabel(client.retailSource)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 sm:px-5 py-4">
                      <p
                        className={`text-sm font-mono ${
                          (client.marginMonthlyUsd ?? 0) >= 0 ? "text-emerald-400" : "text-amber-400"
                        }`}
                      >
                        {formatMonthlyUsd(client.marginMonthlyUsd)}
                      </p>
                    </td>
                    <td className="px-4 sm:px-5 py-4">
                      <div>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-body border capitalize ${
                            STATUS_STYLES[client.status] || STATUS_STYLES.suspended
                          }`}
                        >
                          {client.status}
                        </span>
                        {client.status === "canceling" && (
                          <>
                            <p className="text-[10px] text-partner-muted font-body mt-1">
                              Billing stopped
                            </p>
                            {untilDate && (
                              <p className="text-[10px] text-amber-400/90 font-body">
                                Until {untilDate}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 sm:px-5 py-4 text-sm text-partner-muted font-body">
                      {client.createdAt}
                    </td>
                    <td className="px-4 sm:px-5 py-4 text-sm text-partner-muted font-body">
                      {client.lastActivity}
                    </td>
                    <td className="px-4 sm:px-5 py-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => onOpenConsole?.(client)}
                          disabled={!canOpenConsole || isOpeningConsole}
                          title={
                            canOpenConsole
                              ? "Open client console in a new tab"
                              : isProvisioning
                                ? "Console unavailable while client is provisioning"
                                : "Console unavailable for suspended clients"
                          }
                          className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-body font-medium border ${
                            canOpenConsole && !isOpeningConsole
                              ? "bg-[#00D2FF]/10 text-partner-accent border-[#00D2FF]/30 hover:bg-[#00D2FF]/15"
                              : "bg-[#00D2FF]/10 text-partner-accent border-[#00D2FF]/20 cursor-not-allowed opacity-50"
                          }`}
                          data-testid="partner-open-client-console"
                        >
                          {isOpeningConsole ? "Opening…" : "Open console"}
                        </button>
                        {isActive && (
                          <>
                            <button
                              type="button"
                              onClick={() => onSetRetail?.(client)}
                              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-body font-medium text-partner-muted border border-partner bg-transparent hover:text-partner hover:border-[#00D2FF]/30"
                              data-testid="partner-set-retail"
                            >
                              Set retail
                            </button>
                            <button
                              type="button"
                              onClick={() => onChangeTier?.(client)}
                              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-body font-medium text-partner-muted border border-partner bg-transparent hover:text-partner hover:border-[#00D2FF]/30"
                              data-testid="partner-change-tier"
                            >
                              Change tier
                            </button>
                            <button
                              type="button"
                              onClick={() => onStopBilling?.(client)}
                              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-body font-medium text-amber-300/90 border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-500/50"
                              data-testid="partner-stop-billing"
                            >
                              Stop billing
                            </button>
                          </>
                        )}
                        {isCanceling && (
                          <button
                            type="button"
                            onClick={() => onResumeBilling?.(client)}
                            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-body font-medium text-emerald-300/90 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 hover:border-emerald-500/50"
                            data-testid="partner-resume-billing"
                          >
                            Resume billing
                          </button>
                        )}
                        {isSuspended && (
                          <button
                            type="button"
                            onClick={() => onReactivate?.(client)}
                            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-body font-medium text-emerald-300/90 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 hover:border-emerald-500/50"
                            data-testid="partner-reactivate"
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientRosterTable;
